/**
 * NewCourseBanner — friendly, animated announcement shown on the
 * Doserne home page while the current user has not yet passed the
 * open HSE intro course (order === 1).
 *
 * • Auto-hides once the backend reports `progress.status === "passed"`.
 * • Persists across sessions — no localStorage dismiss. The trigger
 *   is purely progress state, so once the worker completes the quiz
 *   the banner disappears on the next home visit.
 * • Trilingual copy (RO / NO / EN) via the shared LangProvider.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, ArrowRight, Sparkles, BadgeCheck } from "lucide-react";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_DICT = {
  ro: { label: "Bine ai venit",       sub: "Cursul de introducere — trecut" },
  no: { label: "Velkommen fullført",  sub: "Velkomstkurs — bestått" },
  en: { label: "Welcome Certified",   sub: "Welcome course — passed" },
};

function HseCertifiedBadge({ lang }) {
  const t = BADGE_DICT[lang] || BADGE_DICT.no;
  return (
    <div
      className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-emerald-500 shadow-sm mb-6 sm:mb-8"
      style={{ color: "#fff" }}
      data-testid="welcome-certified-badge"
    >
      <BadgeCheck className="w-4 h-4" strokeWidth={2.6} />
      <span className="text-xs font-black tracking-wide">{t.label}</span>
      <span className="text-[10px] opacity-80 font-medium hidden sm:inline">· {t.sub}</span>
    </div>
  );
}

const DICT = {
  ro: {
    tag: "Nou",
    title: "Un curs nou este disponibil pentru toată lumea",
    subtitle: "Deschide cursul HSE de bază — durează câteva minute și primești certificatul instant.",
    cta: "Începe acum",
    toast_msg: "🎓 Un curs nou este disponibil în tabul Cursuri!",
    toast_action: "Deschide",
  },
  no: {
    tag: "Nytt",
    title: "Et nytt kurs er tilgjengelig for alle",
    subtitle: "Ta HSE-introduksjonen — noen få minutter og du får sertifikatet umiddelbart.",
    cta: "Start nå",
    toast_msg: "🎓 Et nytt kurs er tilgjengelig i Kurs-fanen!",
    toast_action: "Åpne",
  },
  en: {
    tag: "New",
    title: "A new course is available for everyone",
    subtitle: "Take the HSE intro — a few minutes and you get the certificate instantly.",
    cta: "Start now",
    toast_msg: "🎓 A new course is available in the Courses tab!",
    toast_action: "Open",
  },
};

export default function NewCourseBanner({ authHeader }) {
  const { lang } = useLang();
  const t = DICT[lang] || DICT.no;
  const [course, setCourse] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/kurs`, authHeader());
        if (cancelled) return;
        // Grab the first course (order === 1) — the one we unlocked
        // for every worker. If it's not there yet, silently no-op.
        const first = (data.courses || []).find((c) => c.order === 1) || null;
        setCourse(first);

        // One-time pop-out toast per session for workers who haven't
        // finished the intro yet. Guarded by sessionStorage so it
        // doesn't nag on every navigation inside the same session.
        const status = first?.progress?.status || "not_started";
        if (first && status !== "passed") {
          try {
            const shown = sessionStorage.getItem("DOSERNE_NEW_COURSE_TOAST_v1");
            if (!shown) {
              const dict = DICT[lang] || DICT.no;
              toast(dict.toast_msg, {
                action: {
                  label: dict.toast_action,
                  onClick: () => { window.location.href = `/doserne/kurs/${first.code}`; },
                },
                duration: 8000,
              });
              sessionStorage.setItem("DOSERNE_NEW_COURSE_TOAST_v1", "1");
            }
          } catch { /* sessionStorage may be blocked — no big deal */ }
        }
      } catch { /* silent — banner just won't show */ }
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [authHeader, lang]);

  if (!ready || !course) return null;
  const status = course.progress?.status || "not_started";
  if (status === "passed") return <HseCertifiedBadge lang={lang} />;

  return (
    <Link
      to={`/doserne/kurs/${course.code}`}
      className="block relative overflow-hidden rounded-2xl mb-6 sm:mb-8 group focus:outline-none focus:ring-2 focus:ring-emerald-400"
      data-testid="new-course-banner"
    >
      {/* Gradient + subtle grain for texture */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500" />
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
      <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-cyan-300/30 blur-2xl" />

      <div className="relative p-4 sm:p-5 flex items-center gap-4">
        <div
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center flex-shrink-0 shadow-lg animate-doserne-course-pulse"
          style={{ color: "#fff" }}
          aria-hidden
        >
          <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.2} />
        </div>

        <div className="flex-1 min-w-0" style={{ color: "#fff" }}>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/30 backdrop-blur-sm text-[10px] font-black uppercase tracking-widest">
            <Sparkles className="w-3 h-3" />
            {t.tag}
          </div>
          <div className="mt-1 text-base sm:text-lg font-black leading-tight">{t.title}</div>
          <div className="text-xs sm:text-sm text-white/85 leading-snug mt-0.5">{t.subtitle}</div>
        </div>

        <div
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white text-emerald-700 text-sm font-black shadow-lg group-hover:translate-x-0.5 transition-transform flex-shrink-0"
          data-testid="new-course-banner-cta"
        >
          {t.cta}
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}
